import { useEffect, useMemo, useState } from 'react';
import { createConversation, refreshUsers, useStore } from '../store.ts';
import { languageName, useT, useUiLocale } from '../i18n.ts';
import { Dialog } from './Dialog.tsx';
import { Seal } from './common.tsx';
import type { MixedOrgsDetails } from '@tc/contracts';
import { MixedOrgsWarning, mixedOrgs } from './MixedOrgsWarning.tsx';

export function NewChatDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const ui = useUiLocale();
  const me = useStore((s) => s.me)!;
  const users = useStore((s) => s.users);
  useEffect(() => void refreshUsers(), []); // people who joined after this page was opened
  const [mode, setMode] = useState<'dm' | 'group'>('dm');
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const people = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return Object.values(users)
      .filter((u) => u.id !== me.id && !u.disabled)
      .filter((u) => !f || u.displayName.toLowerCase().includes(f) || (u.organizationName ?? '').toLowerCase().includes(f))
      .sort((a, b) => (a.organizationName ?? '').localeCompare(b.organizationName ?? '') || a.displayName.localeCompare(b.displayName));
  }, [users, me.id, filter]);

  const [mixed, setMixed] = useState<{ details: MixedOrgsDetails; body: Parameters<typeof createConversation>[0] } | null>(null);
  const go = async (body: Parameters<typeof createConversation>[0]) => {
    setBusy(true);
    try {
      await createConversation(body);
      onClose();
    } catch (err) {
      const details = mixedOrgs(err);
      if (details) setMixed({ details, body });
      else throw err;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('newChat')} onClose={onClose}>
      <div className="segmented" role="tablist">
        <button role="tab" aria-selected={mode === 'dm'} className={mode === 'dm' ? 'on' : ''} onClick={() => setMode('dm')}>{t('directMessage')}</button>
        <button role="tab" aria-selected={mode === 'group'} className={mode === 'group' ? 'on' : ''} onClick={() => setMode('group')}>{t('group')}</button>
      </div>
      {mode === 'group' && (
        <label className="field">
          <span>{t('groupName')}</span>
          <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
      )}
      <input type="search" className="filter" placeholder={t('filterPeople')} aria-label={t('filterPeople')} value={filter} onChange={(e) => setFilter(e.target.value)} />
      <ul className="pick-list" role="list">
        {people.map((u) => {
          const on = picked.includes(u.id);
          return (
            <li key={u.id}>
              {mode === 'dm' ? (
                <button className="pick" onClick={() => void go({ type: 'dm', userId: u.id })} disabled={busy}>
                  <Seal locale={u.readingLocale} small title={languageName(ui, u.readingLocale)} />
                  <span className="grow">{u.displayName}</span>
                  <span className="muted small">{u.organizationName}</span>
                </button>
              ) : (
                <label className={`pick ${on ? 'on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => setPicked(on ? picked.filter((x) => x !== u.id) : [...picked, u.id])} />
                  <Seal locale={u.readingLocale} small title={languageName(ui, u.readingLocale)} />
                  <span className="grow">{u.displayName}</span>
                  <span className="muted small">{u.organizationName}</span>
                </label>
              )}
            </li>
          );
        })}
      </ul>
      {mixed && (
        <MixedOrgsWarning
          details={mixed.details}
          busy={busy}
          onConfirm={() => void go({ ...mixed.body, allowMixedOrgs: true } as Parameters<typeof createConversation>[0])}
          onCancel={() => setMixed(null)}
        />
      )}
      {mode === 'group' && (
        <div className="dialog-actions">
          <button className="btn primary" disabled={busy || !title.trim() || picked.length === 0} onClick={() => void go({ type: 'group', title: title.trim(), memberIds: picked })}>
            {t('createGroup')}
          </button>
        </div>
      )}
    </Dialog>
  );
}
